import { useState, useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

/**
 * Banner that prompts users to verify their email
 * Can be dismissed and stores preference in localStorage
 */
export default function VerifyEmailBanner() {
  const { user, sendVerificationEmail } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) {
      const dismissedKey = `banner-dismissed-${user.uid}`;
      const wasDismissed = localStorage.getItem(dismissedKey) === 'true';
      setDismissed(wasDismissed);
    }
  }, [user]);

  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (user) {
      localStorage.setItem(`banner-dismissed-${user.uid}`, 'true');
    }
  };

  const handleResend = async () => {
    setSending(true);
    try {
      await sendVerificationEmail();
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch (error) {
      console.error('Failed to send verification email:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <AlertCircle className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">Email not verified.</span>{' '}
              Please verify your email to ensure you receive important updates and download links.
            </p>
          </div>
          <div className="flex items-center ml-4 space-x-2">
            {sent ? (
              <span className="text-sm text-green-600 font-medium">
                Email sent!
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={sending}
                className="text-sm font-medium text-amber-800 hover:text-amber-900 underline disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="text-amber-600 hover:text-amber-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
